import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  Zap,
  FileText,
  BookOpen,
  Star,
  PoundSterling,
  ArrowRight,
  Clock,
  Smartphone,
} from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const PAGE_URL = "https://thepropertool.com/job-quoting-software";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Job Quoting Software",
  "description": "Job quoting software for builders and tradespeople. Generate accurate, itemised quotes for any building job from a description, photos, or drawings in under 10 minutes.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Job Quoting Software", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is job quoting software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Job quoting software helps builders and tradespeople price construction jobs accurately and quickly. You describe the job (or upload drawings and photos), the software generates a fully itemised scope with quantities and UK market rates, and you export a professional PDF to send to the client. The Proper Tool is job quoting software built specifically for UK residential construction.",
      },
    },
    {
      "@type": "Question",
      "name": "What types of jobs can The Proper Tool quote?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool can quote any common residential building job: extensions (single and double storey), loft conversions, garage conversions, kitchen and bathroom refurbishments, structural alterations, full house renovations, roofing, drainage, landscaping, and general building works. The 31-category cost library covers all major UK residential trades.",
      },
    },
    {
      "@type": "Question",
      "name": "How fast is job quoting software compared to doing it manually?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Manual job quoting in a spreadsheet typically takes 2–4 hours per quote. The Proper Tool generates a fully itemised quote in under 10 minutes. At a billing rate of £40–£60/hour, the time saving covers the software cost within the first quote of the month.",
      },
    },
    {
      "@type": "Question",
      "name": "Can job quoting software read drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool can read uploaded PDFs of architect's drawings and structural plans. It extracts beam sizes, spans, floor areas, and project dimensions from the drawings and uses them to generate a more accurate, fully itemised quote. This is available on the Pro plan.",
      },
    },
    {
      "@type": "Question",
      "name": "Is there free job quoting software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start from £29/month. Fully free tools exist but typically lack UK-specific pricing data, multi-trade categories, or professional PDF export.",
      },
    },
    {
      "@type": "Question",
      "name": "Does job quoting software work for sole traders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool is built for builders and tradespeople of all sizes, including sole traders. The Lite plan at £29/month is sized for a one-person operation: 10 AI quotes per month, unlimited template and manual quotes, professional PDF export, and the full 31-category cost library. No complex setup, no per-seat fees.",
      },
    },
  ],
};

const FEATURES = [
  {
    icon: Zap,
    title: "Quote any building job in minutes",
    description: "Describe the job in plain text, upload drawings, or attach site photos. The AI generates a fully itemised scope with UK market rates in under 10 minutes.",
  },
  {
    icon: BookOpen,
    title: "31 trade categories covered",
    description: "From groundworks to steelwork, carpentry to M&E, roofing to landscaping: every common UK residential trade is included in a single job quote.",
  },
  {
    icon: PoundSterling,
    title: "UK pricing built in",
    description: "462 rated tasks across 31 categories with current UK market rates. Low, Typical, and High bands built in. Override any rate with your own figures.",
  },
  {
    icon: FileText,
    title: "Reads drawings and plans",
    description: "Upload PDFs of architect's drawings. The tool extracts dimensions and floor areas to price the job more accurately.",
  },
  {
    icon: Star,
    title: "Scope validation",
    description: "Built-in review flags missing dependencies and scope gaps (skip hire for a demolition job, tanking for a wet room) before you send.",
  },
  {
    icon: Smartphone,
    title: "Works on any device",
    description: "Fully web-based. Quote on your phone on-site or from your desk at home. No software to install, no laptop required.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Tell the tool about the job",
    description: "Type a description of the project, upload drawings or structural plans, attach site photos, or mix all three. The Proper Tool works from whatever inputs you have.",
  },
  {
    step: "2",
    title: "AI generates the full itemised quote",
    description: "The AI reads your inputs and produces a fully itemised scope: every trade, every line item, with quantities and unit rates from the UK pricing database. Typically done in under 10 minutes.",
  },
  {
    step: "3",
    title: "Review, edit, and send the PDF",
    description: "Review every line, apply or dismiss validation flags, add or remove items, then export a professionally formatted, branded PDF and send it to your client.",
  },
];

const PROJECT_TYPES = [
  "Single-storey extension",
  "Double-storey extension",
  "Loft conversion",
  "Garage conversion",
  "Bathroom refurbishment",
  "Kitchen refurbishment",
  "Structural alteration",
  "Full house renovation",
  "Roofing works",
  "Drainage works",
  "Landscaping",
  "General building",
];

const FAQS = [
  {
    q: "What is job quoting software?",
    a: "Job quoting software helps builders and tradespeople price construction jobs accurately and quickly. You describe the job (or upload drawings and photos), the software generates a fully itemised scope with quantities and UK market rates, and you export a professional PDF to send to the client. The Proper Tool is job quoting software built specifically for UK residential construction.",
  },
  {
    q: "What types of jobs can The Proper Tool quote?",
    a: "The Proper Tool can quote any common residential building job: extensions, loft conversions, garage conversions, kitchen and bathroom refurbishments, structural alterations, full house renovations, roofing, drainage, landscaping, and general building works. The 31-category cost library covers all major UK residential trades.",
  },
  {
    q: "How fast is job quoting software compared to doing it manually?",
    a: "Manual job quoting in a spreadsheet typically takes 2–4 hours per quote. The Proper Tool generates a fully itemised quote in under 10 minutes. At a billing rate of £40–£60/hour, the time saving covers the software cost within the first quote of the month.",
  },
  {
    q: "Can job quoting software read drawings?",
    a: "Yes. The Proper Tool can read uploaded PDFs of architect's drawings and structural plans. It extracts beam sizes, spans, floor areas, and project dimensions from the drawings and uses them to generate a more accurate, fully itemised quote. This is available on the Pro plan.",
  },
  {
    q: "Is there free job quoting software?",
    a: "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start from £29/month.",
  },
  {
    q: "Does job quoting software work for sole traders?",
    a: "Yes. The Proper Tool is built for builders and tradespeople of all sizes, including sole traders. The Lite plan at £29/month gives 10 AI quotes per month, unlimited template and manual quotes, professional PDF export, and the full 31-category cost library. No complex setup, no per-seat fees.",
  },
];

export default function JobQuotingSoftwarePage() {

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Job Quoting Software for Builders and Tradespeople | The Proper Tool"
        description="Job quoting software for UK builders and tradespeople. Generate professional, itemised quotes for any building job from drawings, photos or a description in under 10 minutes. From £29/mo, 14-day free trial."
        canonical="/job-quoting-software"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Job Quoting Software</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Job Quoting Software
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Generate accurate, professional quotes for any building job from drawings, photos or a description in under 10 minutes. Built for UK builders and tradespeople.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/comparisons">Compare alternatives</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · No credit card required</p>
          </div>
        </section>

        {/* Project types */}
        <section className="py-14 border-t border-border/40 bg-muted/20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">Quote any type of building job</h2>
              <p className="text-muted-foreground">All common UK residential project types are covered out of the box.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {PROJECT_TYPES.map((type) => (
                <span key={type} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-background text-sm font-medium">
                  <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                  {type}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="how-it-works">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">How it works</h2>
              <p className="text-lg text-muted-foreground">Three steps from job description to client-ready quote.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-8">
              {HOW_IT_WORKS.map((step) => (
                <div key={step.step} className="relative">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature grid */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="features">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">What good job quoting software does</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">A purpose-built quoting tool does more than a spreadsheet. Here's what The Proper Tool brings to every job quote.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f) => (
                <div key={f.title} className="bg-background rounded-xl border border-border p-6">
                  <f.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-primary mb-2">10 min</p>
                <p className="text-muted-foreground">Average time to generate a full itemised job quote</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">2–4 hrs</p>
                <p className="text-muted-foreground">Time saved versus manual Excel quoting per job</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">£29/mo</p>
                <p className="text-muted-foreground">Starting price, covered by the first quote you send</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="pricing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple, transparent pricing</h2>
              <p className="text-lg text-muted-foreground">Start with a 14-day free trial. No credit card required.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {([
                { name: "Lite", price: "£29", desc: "For sole traders", features: ["10 AI auto-quotes/mo", "Unlimited template & manual quotes", "Professional PDF export", "Full 31-category cost library"], highlight: false },
                { name: "Starter", price: "£59", desc: "For growing businesses", features: ["25 AI auto-quotes/mo", "Everything in Lite", "Pricing Intelligence", "Expert validation pipeline", "Regional pricing"], highlight: true },
                { name: "Pro", price: "£99", desc: "For high-volume builders", features: ["50 AI auto-quotes/mo", "Everything in Starter", "Drawing analysis", "Learned rate adjustments"], highlight: false },
              ] as { name: string; price: string; desc: string; features: string[]; highlight: boolean }[]).map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 flex flex-col ${plan.highlight ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                >
                  <div className="mb-4">
                    <p className="font-bold text-lg">{plan.name}</p>
                    <p className="text-3xl font-bold mt-1">{plan.price}<span className="text-base font-normal text-muted-foreground">/mo</span></p>
                    <p className="text-sm text-muted-foreground mt-1">{plan.desc}</p>
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={plan.highlight ? "default" : "outline"} asChild className="w-full">
                    <Link href="/login">Start free trial</Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related guides</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/quoting-software-for-builders", label: "Quoting Software for Builders" },
                { href: "/quoting-software-for-contractors", label: "Quoting Software for Contractors" },
                { href: "/tradesman-quoting-tool", label: "Tradesman Quoting Tool" },
                { href: "/estimating-software-for-builders", label: "Estimating Software for Builders" },
                { href: "/best-quoting-software", label: "Best Quoting Software for Builders: 2025 Comparison" },
                { href: "/blog/how-to-price-building-jobs", label: "How to Price Building Jobs: A Practical Guide" },
                { href: "/free-trial/", label: "Free Trial" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to quote your next job faster?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join builders and tradespeople using The Proper Tool to generate professional job quotes in minutes, not hours.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free 14-day trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
